import Link from "next/link";
import { CaretRight } from "../Icons/Icons";
import clsx from "clsx";
import { useState, useEffect, useRef } from "react";
import { NavItem } from "./types";

export interface ContentListProps {
  items: NavItem[];
  title?: string;
  onItemSelect?: (item: NavItem) => void;
  selectedId: string | number;
}

const ContentList = ({
  items,
  title,
  selectedId,
  onItemSelect,
}: ContentListProps) => {
  const [, setAnimatingTo] = useState<string | number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = (item: NavItem) => {
    const currentIndex = items.findIndex((i) => i.id === selectedId);
    const targetIndex = items.findIndex((i) => i.id === item.id);

    if (currentIndex === targetIndex) {
      return;
    }

    setAnimatingTo(item.id);
    const step = currentIndex < targetIndex ? 1 : -1;
    let nextIndex = currentIndex + step;

    const animateSequence = () => {
      if (
        (step > 0 && nextIndex <= targetIndex) ||
        (step < 0 && nextIndex >= targetIndex)
      ) {
        const nextItem = items[nextIndex];
        onItemSelect?.(nextItem);

        if (nextIndex !== targetIndex) {
          nextIndex += step;
          timeoutRef.current = setTimeout(animateSequence, 100);
        } else {
          setAnimatingTo(null);
        }
      }
    };

    animateSequence();
  };

  return (
    <nav className="flex flex-col items-start self-stretch gap-[1.6rem]">
      {title && (
        <h2 className="text-[2.8rem] font-medium leading-[2.8rem]">{title}</h2>
      )}

      <ul className="flex flex-col items-start self-stretch max-w-[360px] gap-2 font-normal leading-[24px]">
        {items.map((item: NavItem) => (
          <li
            key={item.id}
            onClick={() => handleItemClick(item)}
            className={clsx(
              "flex items-center justify-between self-stretch",
              "px-4 py-[10px] rounded-lg cursor-pointer",
              "transition-all duration-200",
              {
                "bg-[#232B37] text-[#f9f9fb] max-w-[360px]":
                  selectedId === item.id,
                "bg-white hover:bg-[#DEDEE3]": selectedId !== item.id,
              },
            )}
          >
            {item.href ? (
              <Link
                href={item.href}
                className="flex items-center justify-between w-full"
              >
                <span className="break-words font-[400]">{item.label}</span>
                {selectedId === item.id && (
                  <div>
                    <CaretRight />
                  </div>
                )}
              </Link>
            ) : (
              <>
                <span className="break-words font-[400]">{item.label}</span>
                {selectedId === item.id && (
                  <div>
                    <CaretRight />
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default ContentList;
